import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

export interface ManualRefreshLock {
  current: boolean;
}

/**
 * 「更新中」表示を保つ最小時間 (ms)。
 *
 * 取得が速いと表示が数十msで消え、更新が起きたことを認識できない。
 * 引っ張って更新 (PullRefresh の MIN_REFRESH_DISPLAY_MS) と同じ値にし、
 * ホイール更新でも同じ手応えになるよう揃える。
 */
export const MIN_REFRESH_DISPLAY_MS = 800;

/**
 * 最小表示時間まであと何ms残っているかを返す。
 *
 * 取得が最小表示時間より長引いた場合は 0 を返し、待たずに解除する。
 */
export function remainingRefreshDisplayMs(
  startedAt: number,
  now: number,
  minDisplayMs: number = MIN_REFRESH_DISPLAY_MS,
): number {
  return Math.max(0, minDisplayMs - (now - startedAt));
}

export async function runManualRefresh(
  lock: ManualRefreshLock,
  isFetching: boolean,
  refresh: () => Promise<unknown>,
  setIsRefreshing: (isRefreshing: boolean) => void,
  onStarted?: () => void,
): Promise<boolean> {
  if (lock.current || isFetching) return false;

  lock.current = true;
  setIsRefreshing(true);
  try {
    onStarted?.();
    await refresh();
    return true;
  } finally {
    lock.current = false;
    setIsRefreshing(false);
  }
}

interface UseManualRefreshResult {
  isManualRefreshing: boolean;
  refreshManually: (onStarted?: () => void) => Promise<boolean>;
}

export function useManualRefresh(
  refresh: () => Promise<unknown>,
  isFetching: boolean,
  minDisplayMs: number = MIN_REFRESH_DISPLAY_MS,
): UseManualRefreshResult {
  const lockRef = useRef(false);
  const isFetchingRef = useRef(isFetching);
  const refreshRef = useRef(refresh);
  const startedAtRef = useRef(0);
  const holdTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);

  useLayoutEffect(() => {
    isFetchingRef.current = isFetching;
    refreshRef.current = refresh;
  }, [isFetching, refresh]);

  useEffect(
    () => (): void => {
      if (holdTimerRef.current) clearTimeout(holdTimerRef.current);
    },
    [],
  );

  /**
   * 取得の完了と表示の解除を切り離す。
   *
   * 解除だけを最小表示時間まで遅らせ、取得処理そのものは待たせない。
   * runManualRefresh のロック解放も遅らせないため、連続更新の応答性は変わらない。
   */
  const setRefreshingWithMinDisplay = useCallback(
    (refreshing: boolean): void => {
      if (holdTimerRef.current) {
        clearTimeout(holdTimerRef.current);
        holdTimerRef.current = undefined;
      }
      if (refreshing) {
        startedAtRef.current = performance.now();
        setIsManualRefreshing(true);
        return;
      }
      const remaining = remainingRefreshDisplayMs(
        startedAtRef.current,
        performance.now(),
        minDisplayMs,
      );
      if (remaining === 0) {
        setIsManualRefreshing(false);
        return;
      }
      holdTimerRef.current = setTimeout((): void => {
        holdTimerRef.current = undefined;
        setIsManualRefreshing(false);
      }, remaining);
    },
    [minDisplayMs],
  );

  const refreshManually = useCallback(
    (onStarted?: () => void): Promise<boolean> =>
      runManualRefresh(
        lockRef,
        isFetchingRef.current,
        refreshRef.current,
        setRefreshingWithMinDisplay,
        onStarted,
      ),
    [setRefreshingWithMinDisplay],
  );

  return { isManualRefreshing, refreshManually };
}
